import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * 页头动作槽：把「页面级动作」交给页头渲染，而不是让它们在内容区顶部另占一行。
 *
 * 要解决的问题：列表页常把「新建 X / 导出 / 刷新 / 模板」放在内容顶部的工具条里
 * （`ProTable toolbar`、`FilterBar extra`、或独立一行 `Space`）。这类工具条各自占一整行（约 44px），
 * 上提到页头 `extra` 后该行消失——表头列名行随之上移同样高度。
 *
 * 为什么需要这个槽：页头由页面组件（`PageContainer` / `SettingsPage` 的唯一持有者）渲染，
 * 动作却常由子视图（Tab 子页、同 URL 家族的视图组件）持有，两者之间需要一个通道。
 *
 * 为什么用「挂载点 DOM + portal」而不是「子视图把 ReactNode 注册进父级 state」：
 * 子视图每次渲染都会产出新的元素身份，走 state 注册就会形成「子渲染 → 父 setState → 子渲染」的回环
 * （`SettingsPage` 与 `settingsHeaderSlot` 里记录过同类 "Maximum update depth exceeded"）；
 * portal 的挂载点由父级固定、内容随子视图自身渲染更新，既不需要父级重渲染，也不会有过期闭包。
 *
 * 用法：
 *   父级：`const slot = usePageHeaderActionsHost();`
 *         `<PageContainer extra={slot.extra}><PageHeaderActionsProvider target={slot.target}>…正文…`
 *   子视图：`<PageHeaderActions><Space>…动作按钮…</Space></PageHeaderActions>`
 * 未提供槽时（独立渲染、内嵌渲染等场景）子视图原位渲染，行为与改造前一致。
 */
const SlotContext = createContext<{ target: HTMLElement | null } | null>(null);

/** 页头侧的挂载点容器（放进 `PageContainer` / `SettingsPage` 的 `extra`） */
export function PageHeaderActionsTarget({ onTarget }: { onTarget: (el: HTMLDivElement | null) => void }) {
  // 行内样式而非全局类：槽只是页头 extra 里的一个 flex 容器，不值得为它新增全局样式
  return <div ref={onTarget} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }} />;
}

/** 页面级 hook：返回放进 `extra` 的挂载点与包住正文的 Provider 所需的 target */
export function usePageHeaderActionsHost() {
  const [target, setTarget] = useState<HTMLDivElement | null>(null);
  return { extra: <PageHeaderActionsTarget onTarget={setTarget} />, target };
}

export function PageHeaderActionsProvider({ target, children }: { target: HTMLElement | null; children: ReactNode }) {
  // value 必须 memo：否则每次渲染新建对象，消费方全部重渲染（同 `EmbeddedPage` 的注释所述坑）
  const value = useMemo(() => ({ target }), [target]);
  return <SlotContext.Provider value={value}>{children}</SlotContext.Provider>;
}

/**
 * 子视图侧的包壳：有页头槽就送到页头，否则原位渲染。
 *
 * `provider 存在但挂载点尚未就绪`（父级 ref 回调所在的渲染帧）时先不渲染：
 * 这一帧把动作先画在内容里再搬走会闪一下，安静等待比闪烁好。
 */
export default function PageHeaderActions({ children }: { children: ReactNode }) {
  const ctx = useContext(SlotContext);
  if (!ctx) return <>{children}</>;
  return ctx.target ? createPortal(children, ctx.target) : null;
}
